import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { deliveryAPI, cityAPI, workAreaAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function DeliveryProfile() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);

  // Edit modes
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingWork, setEditingWork] = useState(false);
  const [editingBank, setEditingBank] = useState(false);

  // Form data
  const [personalForm, setPersonalForm] = useState({ name: '', phone: '' });
  const [workForm, setWorkForm] = useState({ workCity: '', workAreas: [] });
  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    branch: '',
    accountType: 'savings',
    upiId: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Available cities and work areas for dropdown
  const [availableCities, setAvailableCities] = useState([]);
  const [availableWorkAreas, setAvailableWorkAreas] = useState([]);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData(user);
      setPersonalForm({ name: user.name || '', phone: user.phone || '' });

      const dp = user.deliveryPartner || {};
      setWorkForm({
        workCity: dp.workCity || '',
        workAreas: dp.workAreas || []
      });

      // Fetch work areas if work city is set
      if (dp.workCity) {
        fetchWorkAreas(dp.workCity);
      }

      const bank = user.bankDetails || {};
      setBankForm({
        accountHolderName: bank.accountHolderName || '',
        accountNumber: bank.accountNumber || '',
        ifscCode: bank.ifscCode || '',
        bankName: bank.bankName || '',
        branch: bank.branch || '',
        accountType: bank.accountType || 'savings',
        upiId: bank.upiId || '',
        address: bank.address || '',
        city: bank.city || '',
        state: bank.state || '',
        pincode: bank.pincode || ''
      });

      setLoading(false);
    }
  }, [user]);

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getActiveCities();
      setAvailableCities(response.data.data.cities);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
    }
  };

  const fetchWorkAreas = async (city) => {
    try {
      const response = await workAreaAPI.getWorkAreasByCity(city);
      setAvailableWorkAreas(response.data.data.workAreas);
    } catch (err) {
      console.error('Failed to fetch work areas:', err);
      setAvailableWorkAreas([]);
    }
  };

  const handleCityChange = (city) => {
    setWorkForm({
      workCity: city,
      workAreas: []
    });
    fetchWorkAreas(city);
  };

  const handleWorkAreaToggle = (areaName) => {
    setWorkForm(prev => {
      const areas = prev.workAreas.includes(areaName)
        ? prev.workAreas.filter(a => a !== areaName)
        : [...prev.workAreas, areaName];
      return { ...prev, workAreas: areas };
    });
  };

  const handleUpdatePersonal = async (e) => {
    e.preventDefault();
    try {
      const response = await deliveryAPI.updatePersonalInfo(personalForm);
      if (response.data.success) {
        alert('Personal information updated successfully!');
        setEditingPersonal(false);
        await refreshUser();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update personal information');
    }
  };

  const handleUpdateWork = async (e) => {
    e.preventDefault();
    try {
      const response = await deliveryAPI.updateWorkAreas(workForm);
      if (response.data.success) {
        alert('Work areas updated successfully!');
        setEditingWork(false);
        await refreshUser();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update work areas');
    }
  };

  const handleUpdateBank = async (e) => {
    e.preventDefault();
    try {
      const response = await deliveryAPI.updateBankDetails(bankForm);
      if (response.data.success) {
        alert('Bank details updated successfully!');
        setEditingBank(false);
        await refreshUser();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update bank details');
    }
  };


  if (loading) return <div className="loading">🔄 Loading profile...</div>;

  const dp = profileData?.deliveryPartner || {};
  const vehicleIcons = {
    motorcycle: '🏍️',
    scooty: '🛵',
    scooter: '🛵',
    ev: '⚡',
    bike: '🚲',
    car: '🚗'
  };

  const statusColors = {
    pending: '#FFA500',
    payment_pending: '#FF6B6B',
    under_review: '#4A90E2',
    approved: '#10B981',
    rejected: '#EF4444'
  };

  const statusLabels = {
    pending: 'Pending',
    payment_pending: 'Payment Pending',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected'
  };

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            👤 My Profile
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            View and update your delivery partner information
          </p>
        </div>

        {/* Onboarding Status Banner */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-2xl)',
          background: `linear-gradient(135deg, ${statusColors[dp.onboardingStatus]} 0%, ${statusColors[dp.onboardingStatus]}dd 100%)`,
          color: 'white',
          border: 'none'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 'var(--space-lg)'
          }}>
            <div style={{ flex: 1 }}>
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                Status: {statusLabels[dp.onboardingStatus] || 'Unknown'}
              </h2>
              <p style={{ margin: 0, opacity: 0.95 }}>
                {dp.isApproved
                  ? '✅ You are approved to accept deliveries'
                  : '⏳ Your application is being processed'}
              </p>
            </div>
            {dp.approvedAt && (
              <div style={{
                padding: 'var(--space-md)',
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 'var(--radius-md)'
              }}>
                <div style={{ fontSize: 'var(--font-size-sm)', opacity: 0.9, marginBottom: 'var(--space-xs)' }}>
                  Approved On
                </div>
                <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700' }}>
                  {new Date(dp.approvedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Personal Information */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-lg)'
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              color: 'var(--gray-900)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              👤 Personal Information
            </h3>
            {!editingPersonal ? (
              <button
                onClick={() => setEditingPersonal(true)}
                className="btn btn-sm"
                style={{ background: 'var(--primary)', color: 'white' }}
              >
                ✏️ Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => setEditingPersonal(false)}
                  className="btn btn-sm"
                  style={{ background: 'var(--gray-400)', color: 'white' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {!editingPersonal ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-lg)'
            }}>
              <div className="profile-field">
                <div className="profile-label">Name</div>
                <div className="profile-value">{profileData?.name || 'N/A'}</div>
              </div>
              <div className="profile-field">
                <div className="profile-label">Email</div>
                <div className="profile-value">{profileData?.email || 'N/A'}</div>
              </div>
              <div className="profile-field">
                <div className="profile-label">Phone Number</div>
                <div className="profile-value">{profileData?.phone || 'N/A'}</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleUpdatePersonal}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 'var(--space-lg)',
                marginBottom: 'var(--space-lg)'
              }}>
                <div>
                  <label className="profile-label">Name *</label>
                  <input
                    type="text"
                    value={personalForm.name}
                    onChange={(e) => setPersonalForm({ ...personalForm, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: 'var(--space-sm)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-base)'
                    }}
                  />
                </div>
                <div>
                  <label className="profile-label">Phone Number *</label>
                  <input
                    type="tel"
                    value={personalForm.phone}
                    onChange={(e) => setPersonalForm({ ...personalForm, phone: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: 'var(--space-sm)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-base)'
                    }}
                  />
                </div>
                <div>
                  <label className="profile-label">Email (Cannot be changed)</label>
                  <input
                    type="email"
                    value={profileData?.email || ''}
                    disabled
                    style={{
                      width: '100%',
                      padding: 'var(--space-sm)',
                      border: '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-base)',
                      background: 'var(--gray-100)',
                      color: 'var(--gray-600)'
                    }}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn"
                style={{ background: 'var(--success)', color: 'white' }}
              >
                💾 Save Changes
              </button>
            </form>
          )}
        </div>

        {/* Work Details */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-lg)'
          }}>
            <h3 style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '700',
              color: 'var(--gray-900)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              🗺️ Work Details
            </h3>
            {!editingWork ? (
              <button
                onClick={() => setEditingWork(true)}
                className="btn btn-sm"
                style={{ background: 'var(--primary)', color: 'white' }}
              >
                ✏️ Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => setEditingWork(false)}
                  className="btn btn-sm"
                  style={{ background: 'var(--gray-400)', color: 'white' }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {!editingWork ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-lg)'
            }}>
              <div className="profile-field">
                <div className="profile-label">Work City</div>
                <div className="profile-value">{dp.workCity || 'Not Set'}</div>
              </div>
              <div className="profile-field" style={{ gridColumn: 'span 2' }}>
                <div className="profile-label">Work Areas</div>
                <div className="profile-value">
                  {dp.workAreas && dp.workAreas.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                      {dp.workAreas.map((area, index) => (
                        <span key={index} style={{
                          padding: 'var(--space-xs) var(--space-sm)',
                          background: 'var(--primary)',
                          color: 'white',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          📍 {typeof area === 'string' ? area : area.name}
                        </span>
                      ))}
                    </div>
                  ) : 'No work areas set'}
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleUpdateWork}>
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label className="profile-label">Work City *</label>
                <select
                  value={workForm.workCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm)',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-base)'
                  }}
                >
                  <option value="">Select City</option>
                  {availableCities.map((city, index) => (
                    <option key={index} value={city.name}>
                      {city.name}, {city.state}
                    </option>
                  ))}
                </select>
              </div>

              {workForm.workCity && availableWorkAreas.length > 0 && (
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <label className="profile-label">Work Areas *</label>
                  <div style={{
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    border: '2px solid #3b82f6',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-md)',
                    marginBottom: 'var(--space-md)'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: 'var(--space-xs)' }}>
                      💰 Surge Pricing Information
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: '#1e3a8a' }}>
                      Click on areas to select/deselect. Surge multipliers show real-time demand!
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-md)'
                  }}>
                    {availableWorkAreas.map((area) => {
                      const surge = area.currentSurgeMultiplier || 1.0;
                      const isSelected = workForm.workAreas.includes(area.name);
                      const getSurgeColor = (surge) => {
                        if (surge >= 1.4) return '#ef4444';
                        if (surge >= 1.2) return '#f59e0b';
                        if (surge >= 1.1) return '#eab308';
                        return '#10b981';
                      };

                      return (
                        <div
                          key={area._id}
                          onClick={() => handleWorkAreaToggle(area.name)}
                          style={{
                            padding: 'var(--space-md)',
                            border: isSelected ? '3px solid var(--primary)' : '2px solid var(--gray-300)',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            background: isSelected ? 'var(--primary-light)' : 'white',
                            transition: 'all 0.2s',
                            position: 'relative'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-xs)' }}>
                            <div style={{ fontWeight: '600', fontSize: 'var(--font-size-base)' }}>
                              {area.name}
                            </div>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-full)',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600',
                              background: getSurgeColor(surge),
                              color: 'white'
                            }}>
                              {surge.toFixed(1)}x
                            </span>
                          </div>
                          {area.description && (
                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                              {area.description}
                            </div>
                          )}
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'var(--success)',
                              color: 'white',
                              borderRadius: 'var(--radius-full)',
                              padding: '4px 8px',
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: '600'
                            }}>
                              ✓ Selected
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 'var(--space-md)', fontSize: 'var(--font-size-sm)', fontWeight: '600' }}>
                    Selected: {workForm.workAreas.length > 0 ? workForm.workAreas.join(', ') : 'None'}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn"
                style={{ background: 'var(--success)', color: 'white' }}
              >
                💾 Save Changes
              </button>
            </form>
          )}
        </div>

        {/* Vehicle Details */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            🚗 Vehicle Information
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 'var(--space-lg)'
          }}>
            <div className="profile-field">
              <div className="profile-label">Vehicle Type</div>
              <div className="profile-value">
                {vehicleIcons[dp.vehicleType] || '🚗'} {dp.vehicleType ? dp.vehicleType.charAt(0).toUpperCase() + dp.vehicleType.slice(1) : 'Not Set'}
              </div>
            </div>
            <div className="profile-field">
              <div className="profile-label">Vehicle Number</div>
              <div className="profile-value">{dp.vehicleNumber || 'Not Set'}</div>
            </div>
            <div className="profile-field">
              <div className="profile-label">License Number</div>
              <div className="profile-value">{dp.licenseNumber || 'Not Set'}</div>
            </div>
          </div>
        </div>

        {/* ID Documents */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            📄 ID Documents
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: 'var(--space-lg)'
          }}>
            <div className="profile-field">
              <div className="profile-label">ID Type</div>
              <div className="profile-value">{dp.idType === 'aadhar' ? '🆔 Aadhar' : dp.idType === 'pan' ? '🆔 PAN' : 'Not Set'}</div>
            </div>
            {dp.idType === 'aadhar' && (
              <>
                <div className="profile-field">
                  <div className="profile-label">Aadhar Number</div>
                  <div className="profile-value">{dp.aadharNumber ? `XXXX-XXXX-${dp.aadharNumber.slice(-4)}` : 'Not Set'}</div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">Aadhar Linked Mobile</div>
                  <div className="profile-value">{dp.aadharLinkedMobile || 'Not Set'}</div>
                </div>
              </>
            )}
            {dp.idType === 'pan' && (
              <div className="profile-field">
                <div className="profile-label">PAN Number</div>
                <div className="profile-value">{dp.panNumber || 'Not Set'}</div>
              </div>
            )}
          </div>
        </div>

        {/* Bank Details */}
        {(profileData?.bankDetails || editingBank) && (
          <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-lg)'
            }}>
              <h3 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                color: 'var(--gray-900)',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                🏦 Bank Details
              </h3>
              {!editingBank ? (
                <button
                  onClick={() => setEditingBank(true)}
                  className="btn btn-sm"
                  style={{ background: 'var(--primary)', color: 'white' }}
                >
                  ✏️ Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <button
                    onClick={() => setEditingBank(false)}
                    className="btn btn-sm"
                    style={{ background: 'var(--gray-400)', color: 'white' }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {!editingBank ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: 'var(--space-lg)'
              }}>
                <div className="profile-field">
                  <div className="profile-label">Account Holder Name</div>
                  <div className="profile-value">{profileData.bankDetails?.accountHolderName || 'Not Set'}</div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">Account Number</div>
                  <div className="profile-value">
                    {profileData.bankDetails?.accountNumber
                      ? `XXXX${profileData.bankDetails.accountNumber.slice(-4)}`
                      : 'Not Set'}
                  </div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">IFSC Code</div>
                  <div className="profile-value">{profileData.bankDetails?.ifscCode || 'Not Set'}</div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">Bank Name</div>
                  <div className="profile-value">{profileData.bankDetails?.bankName || 'Not Set'}</div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">Branch</div>
                  <div className="profile-value">{profileData.bankDetails?.branch || 'Not Set'}</div>
                </div>
                <div className="profile-field">
                  <div className="profile-label">Account Type</div>
                  <div className="profile-value">
                    {profileData.bankDetails?.accountType
                      ? profileData.bankDetails.accountType.charAt(0).toUpperCase() + profileData.bankDetails.accountType.slice(1)
                      : 'Not Set'}
                  </div>
                </div>
                {profileData.bankDetails?.upiId && (
                  <div className="profile-field">
                    <div className="profile-label">UPI ID</div>
                    <div className="profile-value">{profileData.bankDetails.upiId}</div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleUpdateBank}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: 'var(--space-lg)',
                  marginBottom: 'var(--space-lg)'
                }}>
                  <div>
                    <label className="profile-label">Account Holder Name *</label>
                    <input
                      type="text"
                      value={bankForm.accountHolderName}
                      onChange={(e) => setBankForm({ ...bankForm, accountHolderName: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="profile-label">Account Number *</label>
                    <input
                      type="text"
                      value={bankForm.accountNumber}
                      onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="profile-label">IFSC Code *</label>
                    <input
                      type="text"
                      value={bankForm.ifscCode}
                      onChange={(e) => setBankForm({ ...bankForm, ifscCode: e.target.value.toUpperCase() })}
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="profile-label">Bank Name *</label>
                    <input
                      type="text"
                      value={bankForm.bankName}
                      onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="profile-label">Branch</label>
                    <input
                      type="text"
                      value={bankForm.branch}
                      onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })}
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="profile-label">Account Type</label>
                    <select
                      value={bankForm.accountType}
                      onChange={(e) => setBankForm({ ...bankForm, accountType: e.target.value })}
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    >
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                    </select>
                  </div>
                  <div>
                    <label className="profile-label">UPI ID</label>
                    <input
                      type="text"
                      value={bankForm.upiId}
                      onChange={(e) => setBankForm({ ...bankForm, upiId: e.target.value })}
                      placeholder="username@upi"
                      style={{
                        width: '100%',
                        padding: 'var(--space-sm)',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-base)'
                      }}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn"
                  style={{ background: 'var(--success)', color: 'white' }}
                >
                  💾 Save Changes
                </button>
              </form>
            )}
          </div>
        )}

        {/* Performance Stats */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: '700',
            marginBottom: 'var(--space-lg)',
            color: 'var(--gray-900)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }}>
            📊 Performance
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-lg)'
          }}>
            <div className="stat-card" style={{ borderLeftColor: 'var(--primary)' }}>
              <div className="stat-icon">📦</div>
              <div className="stat-value">{dp.totalDeliveries || 0}</div>
              <div className="stat-label">Total Deliveries</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: 'var(--warning)' }}>
              <div className="stat-icon">⭐</div>
              <div className="stat-value">{dp.rating || 0}</div>
              <div className="stat-label">Rating</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: dp.isAvailable ? 'var(--success)' : 'var(--danger)' }}>
              <div className="stat-icon">{dp.isAvailable ? '🟢' : '🔴'}</div>
              <div className="stat-value">{dp.isAvailable ? 'Online' : 'Offline'}</div>
              <div className="stat-label">Current Status</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .profile-field {
          padding: var(--space-md);
          background: var(--gray-50);
          borderRadius: var(--radius-md);
          border-left: 3px solid var(--primary);
        }

        .profile-label {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin-bottom: var(--space-xs);
          font-weight: 600;
          display: block;
        }

        .profile-value {
          font-size: var(--font-size-base);
          color: var(--gray-900);
          font-weight: 500;
        }
      `}</style>
    </div>
  );
}

export default DeliveryProfile;
